import { useTranslation } from 'react-i18next'
import React from 'react'

import { Grid } from '../../../../../packages/kiti/components/structure/Grid'
import { useFormState } from '../../../../../packages/kiti/components/form/form/FormHandler'
import { useIntegration } from '../../_load/IntegrationLoader'
import { getSubdomain } from '../../../../../utils/helpers/url'
import { isActionAllowedForEntity } from '../../_helpers/allowedActions'
import DynamicSentence from './DynamicSentence'
import { getUpdateTarget, TARGET_CURRENT } from './updateChildServiceOrder/UpdateChildServiceOrderSentence'
import EditableSentence from '../EditableSentence'
import Text from '../../../../../packages/kiti/components/presentation/Text'
import { useModalData } from '../../../../../components/modals/ModalsContext'

const SCHEDULE_INSERT_WORKSPACES = ['v2', 'arehs']

const useOptions = t => [
  { id: 'update_status', name: t('actions.update_status'), entities: [] },
  { id: 'update_parent_status', name: t('actions.update_parent_status'), entities: [] },
  { id: 'update_child_service_order', name: t('actions.update_child_service_order'), entities: [] },
  { id: 'update_place', name: t('actions.update_place'), entities: [] },
  { id: 'update_asset', name: t('actions.update_asset'), entities: [] },
  { id: 'email', name: t('actions.email'), entities: [] },
  { id: 'webhook', name: t('actions.webhook'), entities: [] },
  { id: 'insert_service_order', name: t('actions.insert_service_order'), entities: [] },
  { id: 'insert_service_orders', name: t('actions.insert_service_orders'), initialPayload: [{}, {}], entities: [] },
  {
    id: 'complete_service_orders',
    name: t('actions.complete_service_orders'),
    initialPayload: [{}, {}],
    entities: ['service_order']
  },
  {
    id: 'complete_non_compliances',
    name: t('actions.complete_non_compliances'),
    initialPayload: [{}, {}],
    entities: ['non_compliance']
  },
  { id: 'schedule_insert_service_order', name: t('actions.schedule_insert_service_order'), entities: [] },
  {
    id: 'schedule_insert_service_orders',
    name: t('actions.schedule_insert_service_orders'),
    initialPayload: [{}, {}],
    entities: []
  },
  { id: 'update_service_order_name', name: t('actions.update_service_order_name'), entities: [] },
  { id: 'update_service_order_user_or_group', name: t('actions.update_service_order_user_or_group'), entities: [] },
  { id: 'insert_place', name: t('actions.insert_place'), entities: [] },
  { id: 'google_calendar_insert_event', name: t('actions.google_calendar_insert_event'), service: 'google' },
  {
    id: `slack_channel_message`,
    name: t('actions.slack_channel_message'),
    service: 'slack'
  },
  {
    id: `slack_direct_message`,
    name: t('actions.slack_direct_message'),
    service: 'slack'
  },
  {
    id: 'd4sign_gen_pdf_and_send_to_sing',
    name: t('actions.d4sign_gen_pdf_and_send_to_sing'),
    service: 'd4sign'
  },
  {
    id: 'd4sign_attach_document',
    name: t('actions.d4sign_attach_document'),
    service: 'd4sign',
    initialPayload: {
      origin: {
        name: t('actions.originActivity'),
        value: {
          id: '::{{service_order.id}}',
          checklist_id: '::{{service_order.checklist.id}}'
        }
      }
    }
  },
  {
    id: 'd4sign_send_to_sing',
    name: t('actions.d4sign_send_to_sing'),
    service: 'd4sign',
    initialPayload: {
      signers: [{}, {}]
    }
  },
  {
    id: 'whatsapp_by_twilio',
    name: t('actions.whatsapp_by_twilio'),
    service: 'twilio'
  },
  {
    id: 'sms_by_twilio',
    name: t('actions.sms_by_twilio'),
    service: 'twilio'
  },
  {
    id: 'whatsapp_by_chat_pro',
    name: t('actions.whatsapp_by_chat_pro'),
    service: 'chat_pro'
  },
  {
    id: 'place_lock',
    name: t('actions.place_lock'),
    entities: ['non_compliance_event', 'non_compliance']
  },
  {
    id: 'set_availability_status',
    name: t('actions.set_availability_status'),
    entities: ['service_order', 'non_compliance', 'event']
  },
  { id: 'assign_tag', name: t('actions.assign_tag'), entities: [] },
  { id: 'remove_tag', name: t('actions.remove_tag'), entities: [] }
]

/** Alinha valor vindo da API (ex.: CHAT_PRO, TWILIO) com o slug usado nas options (chat_pro, twilio). */
const normalizeIntegrationServiceForActions = service => {
  if (!service) return service
  const map = { CHAT_PRO: 'chat_pro', TWILIO: 'twilio', D4SIGN: 'd4sign' }
  return map[service] || service
}

const useOptionsWithEntities = (entities, t, formEntity) => {
  const service = useIntegration()?.service
  const workspace = getSubdomain()
  const canUseScheduleInsert = SCHEDULE_INSERT_WORKSPACES.includes(workspace)
  const OPTIONS = useOptions(t)
  let options = OPTIONS.filter(item => isActionAllowedForEntity(item.id, formEntity))
  if (!canUseScheduleInsert) {
    options = options.filter(
      item => item.id !== 'schedule_insert_service_order' && item.id !== 'schedule_insert_service_orders'
    )
  }
  if (!service) {
    const filteredOptions = options.filter(item => !item.service)
    return filteredOptions.filter(item => {
      if (!item.entities?.length) {
        return true
      }
      return entities?.some(element => {
        return item.entities.includes(element)
      })
    })
  }
  const normalized = normalizeIntegrationServiceForActions(service)
  return options.filter(item => item.service && normalizeIntegrationServiceForActions(item.service) === normalized)
}

const ActionSentence = () => {
  const { entities } = useModalData()
  const { t } = useTranslation('triggers')
  const { handleChange, form } = useFormState()
  const options = useOptionsWithEntities(entities, t, form.entity)
  // "Atualizar Atividade" mostra o destino escolhido na sentença ("... atual" / "... Filha").
  const actionLabel =
    form.action === 'update_child_service_order'
      ? t(
          getUpdateTarget(form.payload) === TARGET_CURRENT
            ? 'updateChildServiceOrder.actionLabel.current'
            : 'updateChildServiceOrder.actionLabel.child'
        )
      : t('actions.' + form.action)

  const onChange = option => {
    if (form.action !== option?.id) {
      handleChange('payload', option?.initialPayload || {})
    }
    handleChange('action', option?.id)
  }

  return (
    <>
      <Grid gap={0.7} align="center">
        <Text size="h2">deve</Text>

        <EditableSentence
          field="action"
          label={actionLabel || '_______'}
          onChange={onChange}
          options={options}
          showTooltip
        />
      </Grid>

      <Grid align="center" wrap>
        <DynamicSentence />
      </Grid>
    </>
  )
}

export default ActionSentence
